import DrawerLayout from "@/Components/Layout/Drawer";
import { APP_ROUTES, apiErrorParser, useAppTranslation } from "@/utils";
import { bnplConsumerApis } from "@payping/api";
import { InstallmentStatus } from "@payping/api/app/api/bnplConsumerTypes";
import type { InstallmentResponse } from "@payping/api/app/api/bnplConsumerTypes";
import { CuToman, FiArrowRight } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { Alert, Button, Card, Divider, Drawer, Flex, Typography, useMessagedAsyncFn } from "@payping/web-components";
import { Link } from "react-router";

interface InstallmentDetailProps {
  open: boolean;
  onClose: () => void;
  data: InstallmentResponse;
}

const HomeOverviewInstallmentDetailsPage = ({ data, onClose, open }: InstallmentDetailProps) => {
  const { t } = useAppTranslation();

  const [, payment] = useMessagedAsyncFn(
    async (trackingCode: string) => {
      try {
        const res = await bnplConsumerApis.installmentPayment({
          installmentCode: data.installmentCode ?? "",
          trackingCode,
        });
        if (res.url) {
          window.location.replace(res.url);
        }
        return res;
      } catch (error: any) {
        const parsedError = await apiErrorParser(error);
        if (parsedError.parsed) {
          throw parsedError.parsed;
        }
        throw parsedError.original;
      }
    },
    [data],
    { loading: false },
    { error: true },
  );

  const handlePay = async () => {
    const trackingCode = data.orderTrackingCode;
    if (!trackingCode) return;
    await payment(trackingCode);
  };

  return (
    <Drawer
      open={open}
      title={
        <Flex align="center" gap={"small"}>
          <Button icon={<FiArrowRight width={14} height={14} />} type="text" onClick={onClose} />
          <Typography.Text>{t("common.invoice")}</Typography.Text>
        </Flex>
      }
      onClose={() => onClose()}
      size={"100%"}
      placement={"bottom"}
      closeIcon={false}
      classNames={{
        header: "tw:py-3! tw:px-4!",
      }}
      styles={{ body: { padding: "0" } }}
    >
      <DrawerLayout className="tw:h-full">
        <Flex vertical className="tw:h-full">
          <Flex vertical className="tw:px-4! tw:pt-8!" gap={"middle"}>
            <Typography.Text>{t("common.paymentInfo")}</Typography.Text>
            <Card size="small">
              <Flex align="center" vertical className="tw:w-full" gap={"small"}>
                <Flex justify="space-between" align="center" className="tw:w-full">
                  <Typography.Text type="secondary">{`${t("common.refundAmount")} :`}</Typography.Text>

                  <Flex align="center">
                    <Typography.Text className="tw:m-0!">
                      {formatLocalizedNumber({
                        value: (data.installmentFinalAmount ?? 0) - (data.delayPenaltyAmount ?? 0),
                      })}
                    </Typography.Text>
                    <CuToman width={24} height={24} />
                  </Flex>
                </Flex>

                {data.delayPenaltyAmount ? (
                  <Flex justify="space-between" align="center" className="tw:w-full">
                    <Typography.Text type="secondary">{`${t("common.delayedPenalty")} :`}</Typography.Text>

                    <Flex align="center">
                      <Typography.Text className="tw:m-0!">
                        {formatLocalizedNumber({ value: data.delayPenaltyAmount ?? 0 })}
                      </Typography.Text>
                      <CuToman width={24} height={24} />
                    </Flex>
                  </Flex>
                ) : null}
              </Flex>
            </Card>
          </Flex>

          <Flex vertical className="tw:mt-auto tw:w-full">
            {!data.isPayable && data.installmentStatus === InstallmentStatus.SalaryDeductionWaiting ? (
              <Flex className="tw:w-full tw:p-4!">
                <Alert
                  className="tw:w-full"
                  type="error"
                  title={
                    <Flex gap={"4px"} vertical>
                      <Typography.Text>{t("home.installmentCard.notPlayable.salaryContent")}</Typography.Text>
                      <Link to={APP_ROUTES.home.orderDetail.url.replace(":code", data.orderTrackingCode ?? "")}>
                        {t("common.installmentsList")}
                      </Link>
                    </Flex>
                  }
                />
              </Flex>
            ) : null}
            {!data.isPayable && data.installmentStatus !== InstallmentStatus.SalaryDeductionWaiting ? (
              <Flex className="tw:w-full tw:p-4!">
                <Alert
                  className="tw:w-full"
                  type="error"
                  title={
                    <Flex gap={"4px"} vertical>
                      <Typography.Text>{t("home.installmentCard.notPlayable.content")}</Typography.Text>
                      <Link to={APP_ROUTES.home.orderDetail.url.replace(":code", data.orderTrackingCode ?? "")}>
                        {t("common.installmentsList")}
                      </Link>
                    </Flex>
                  }
                />
              </Flex>
            ) : null}
            <Divider className="tw:m-0!" />
            <Flex className="tw:w-full tw:px-4! tw:py-2!" align="center" justify="space-between">
              <Flex vertical>
                <Typography.Text type="secondary">{t("common.payableAmount")}</Typography.Text>

                <Flex align="center">
                  <Typography.Text>
                    {formatLocalizedNumber({ value: data.installmentFinalAmount ?? 0 })}
                  </Typography.Text>
                  <CuToman width={24} height={24} />
                </Flex>
              </Flex>

              <Button
                className="tw:px-10!"
                type="primary"
                onClick={data.isPayable ? handlePay : undefined}
                disabled={!data.isPayable}
              >
                {t("common.pay")}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </DrawerLayout>
    </Drawer>
  );
};

export default HomeOverviewInstallmentDetailsPage;
